import React, { useEffect, useState, useMemo } from "react";
import { clientApi } from "../lib/api";
import { FileText, Download, Filter, ChevronDown, ChevronRight } from "lucide-react";

const DOC_CATEGORIES = [
  "Proposta", "Contrato", "Planejamento", "Plano de Mídia",
  "Boleto", "Nota Fiscal", "Boleto Fornecedor", "NF Fornecedor",
  "Peça Entregue", "Reunião de Atendimento", "Cotação de Mídia",
  "Cotação de Mídia Aprovada", "Orçamento de Produção", "Orçamento Aprovado",
];

export default function Documentos() {
  const [products, setProducts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [docs, setDocs] = useState([]);
  const [category, setCategory] = useState("");
  const [productId, setProductId] = useState("");
  const [campaignId, setCampaignId] = useState("");
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    Promise.all([clientApi.get("/products"), clientApi.get("/campaigns")]).then(([p, c]) => {
      setProducts(p.data); setCampaigns(c.data);
    });
  }, []);

  useEffect(() => {
    const params = {};
    if (category) params.category = category;
    if (productId) params.product_id = productId;
    if (campaignId) params.campaign_id = campaignId;
    clientApi.get("/documents", { params }).then(({ data }) => setDocs(data));
  }, [category, productId, campaignId]);

  // Grouped: Client → Product → Campaign (docs)
  const grouped = useMemo(() => {
    const byProd = {};
    for (const p of products) byProd[p.id] = { product: p, campaigns: {}, orphan: [] };
    for (const c of campaigns) if (byProd[c.product_id]) byProd[c.product_id].campaigns[c.id] = { campaign: c, docs: [] };
    for (const d of docs) {
      if (d.product_id && byProd[d.product_id]) {
        if (d.campaign_id && byProd[d.product_id].campaigns[d.campaign_id]) {
          byProd[d.product_id].campaigns[d.campaign_id].docs.push(d);
        } else {
          byProd[d.product_id].orphan.push(d);
        }
      }
    }
    return byProd;
  }, [products, campaigns, docs]);

  const toggle = (k) => setExpanded((s) => ({ ...s, [k]: !s[k] }));

  return (
    <div className="space-y-6" data-testid="cliente-documentos-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-accent-orange mb-1">Repositório</div>
        <h1 className="font-display text-3xl md:text-4xl">Documentos</h1>
        <p className="text-sm text-neutral-400 mt-2">Propostas, contratos, planejamentos, planos de mídia, boletos e notas fiscais — organizados por Cliente → Produto → Campanha.</p>
      </div>

      {/* FILTROS */}
      <div className="grid md:grid-cols-3 gap-3 border border-white/10 rounded-xl p-4 bg-white/[0.02]" data-testid="documentos-filtros">
        <select value={productId} onChange={(e) => { setProductId(e.target.value); setCampaignId(""); }} className="bg-black border border-white/10 rounded-lg px-3 py-2 text-sm">
          <option value="">Todos os produtos</option>
          {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={campaignId} onChange={(e) => setCampaignId(e.target.value)} className="bg-black border border-white/10 rounded-lg px-3 py-2 text-sm">
          <option value="">Todas as campanhas</option>
          {campaigns.filter((c) => !productId || c.product_id === productId).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="bg-black border border-white/10 rounded-lg px-3 py-2 text-sm">
          <option value="">Todas as categorias</option>
          {DOC_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* ÁRVORE */}
      <div className="space-y-2">
        {Object.entries(grouped).map(([pid, node]) => {
          const totalDocs = Object.values(node.campaigns).reduce((s, x) => s + x.docs.length, 0) + node.orphan.length;
          if (totalDocs === 0) return null;
          const open = expanded[pid] ?? true;
          return (
            <div key={pid} className="border border-white/10 rounded-xl overflow-hidden" data-testid={`docs-product-${pid}`}>
              <button onClick={() => toggle(pid)} className="w-full flex items-center gap-3 p-4 hover:bg-white/[0.02] transition">
                {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <div className="flex-1 text-left">
                  <div className="font-semibold">{node.product.name}</div>
                  <div className="text-[11px] text-neutral-500">{node.product.type} · {totalDocs} documento(s)</div>
                </div>
              </button>
              {open && (
                <div className="border-t border-white/10 p-2 space-y-1">
                  {Object.entries(node.campaigns).map(([cid, cnode]) => cnode.docs.length > 0 && (
                    <div key={cid} className="ml-6 border-l border-white/10 pl-4 py-1">
                      <div className="text-xs uppercase tracking-widest text-accent-orange mb-1.5">{cnode.campaign.name}</div>
                      <div className="space-y-1">
                        {cnode.docs.map((d) => <DocRow key={d.id} d={d} />)}
                      </div>
                    </div>
                  ))}
                  {node.orphan.length > 0 && (
                    <div className="ml-6 border-l border-white/10 pl-4 py-1">
                      <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1.5">Sem campanha</div>
                      {node.orphan.map((d) => <DocRow key={d.id} d={d} />)}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {docs.length === 0 && <div className="text-sm text-neutral-500 border border-dashed border-white/10 rounded-xl p-8 text-center">Nenhum documento nos filtros atuais.</div>}
      </div>
    </div>
  );
}

function DocRow({ d }) {
  return (
    <a
      href={d.url || "#"}
      target={d.url && d.url !== "#" ? "_blank" : undefined}
      rel="noopener noreferrer"
      className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-white/5 group transition"
      data-testid={`doc-row-${d.id}`}
    >
      <FileText className="w-4 h-4 text-neutral-500 group-hover:text-accent-orange" />
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate">{d.title}</div>
        <div className="text-[10px] text-neutral-500 uppercase tracking-widest">{d.category}</div>
      </div>
      <Download className="w-4 h-4 text-neutral-500 group-hover:text-white transition" />
    </a>
  );
}
